import { ArrowDown, ArrowUp } from 'lucide-react';
import { MetricCardProps } from '../types';
import { calculatePercentageChange, formatValue } from '../utils';
import { ChartError, ChartLoading } from './ChartStates';

export function MetricCard({
  value,
  previousValue,
  format = 'number',
  currencyCode,
  loading,
  error,
  className = '',
}: MetricCardProps) {
  // Handle loading state
  if (loading) {
    return (
      <div className={className}>
        <ChartLoading className="h-32" />
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className={className}>
        <ChartError error={error} className="h-32" />
      </div>
    );
  }

  // Format value
  const formattedValue =
    typeof value === 'string'
      ? value
      : formatValue(value, format, currencyCode);

  // Calculate change percentage
  const currentNumeric = typeof value === 'number' ? value : parseFloat(value);
  const previousNumeric =
    typeof previousValue === 'number'
      ? previousValue
      : previousValue
        ? parseFloat(previousValue)
        : undefined;

  const change =
    previousNumeric !== undefined
      ? calculatePercentageChange(currentNumeric, previousNumeric)
      : null;

  return (
    <div className={className}>
      <div className="space-y-2">
        <div className="text-2xl leading-[29px] font-medium tracking-[-0.01em] text-[#141C25]">
          {formattedValue}
        </div>

        {change !== null && (
          <div className="flex items-center gap-1">
            {change > 0 ? (
              <>
                <ArrowUp className="h-4 w-4 text-[#16BC61]" />
                <span className="text-sm leading-[17px] font-medium tracking-[-0.01em] text-[#16BC61]">
                  +{change.toFixed(1)}%
                </span>
              </>
            ) : change < 0 ? (
              <>
                <ArrowDown className="h-4 w-4 text-[#FF4D4D]" />
                <span className="text-sm leading-[17px] font-medium tracking-[-0.01em] text-[#FF4D4D]">
                  {change.toFixed(1)}%
                </span>
              </>
            ) : (
              <span className="text-sm font-medium text-gray-600">
                No change
              </span>
            )}
            <span className="ml-1 text-xs text-gray-500">
              vs previous period
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
